import {
  campaignApiDelete,
  campaignApiGet,
  campaignApiPost,
  type CampaignApiQueryValue,
} from '@/lib/api/campaign-api-client';
import { HttpError } from '@/lib/https';
import type {
  BulkSuppressionDataApi,
  CreateSuppressionRequestApi,
  SuppressionChannelApi,
  SuppressionEntryApi,
  SuppressionEnvelopeApi,
  SuppressionListDataApi,
  SuppressionPageInfoApi,
  SuppressionReasonApi,
} from '../types/suppressions-api.types';
import { parseSuppressionFieldErrors } from '../utils/suppression-api-errors';

export type GetSuppressionsParams = {
  limit?: number;
  offset?: number;
  sort?: 'created' | 'address';
  order?: 'asc' | 'desc';
  q?: string;
  reason?: SuppressionReasonApi;
  channel?: SuppressionChannelApi;
};

const formatSuppressionErrorMessage = (
  response: SuppressionEnvelopeApi<unknown> | null | undefined,
  fallback: string
): string => {
  const fieldErrors = parseSuppressionFieldErrors(response);
  const generic = fieldErrors._generic?.[0];
  if (generic) return generic;

  for (const messages of Object.values(fieldErrors)) {
    if (messages[0]) return messages[0];
  }

  if (typeof response?.message === 'string' && response.message.trim()) {
    return response.message.trim();
  }

  return fallback;
};

const assertSuppressionSuccess = <T>(
  response: SuppressionEnvelopeApi<T> | null | undefined,
  fallback: string
): T => {
  if (!response || typeof response !== 'object') {
    throw new Error(fallback);
  }

  if (response.success === false) {
    throw new Error(formatSuppressionErrorMessage(response, fallback));
  }

  if (response.data === undefined || response.data === null) {
    throw new Error(`${fallback}: missing data payload`);
  }

  return response.data;
};

export const getSuppressionErrorMessage = (error: unknown): string => {
  if (error instanceof HttpError) {
    if (error.status === 403) {
      return 'Please sign in again and retry.';
    }
    const message = formatSuppressionErrorMessage(
      error.error as SuppressionEnvelopeApi<unknown>,
      error.message
    );
    return message || error.message || 'Suppression request failed';
  }

  if (error instanceof Error && error.message) return error.message;
  return 'Suppression request failed';
};

const buildListQuery = (params: GetSuppressionsParams): Record<string, CampaignApiQueryValue> => {
  const query: Record<string, CampaignApiQueryValue> = {
    limit: params.limit,
    offset: params.offset,
    sort: params.sort,
    order: params.order,
  };

  const q = params.q?.trim();
  if (q) query.q = q;
  if (params.reason) query['filter[reason]'] = params.reason;
  if (params.channel) query['filter[channel]'] = params.channel;

  return query;
};

export const getSuppressions = async (
  params: GetSuppressionsParams = {}
): Promise<{ items: SuppressionEntryApi[]; pageInfo: SuppressionPageInfoApi }> => {
  const response = await campaignApiGet<SuppressionEnvelopeApi<SuppressionListDataApi>>(
    '/v1/suppressions',
    buildListQuery(params)
  );
  return assertSuppressionSuccess(response, 'Failed to fetch suppressions');
};

export const createSuppression = async (params: {
  address: string;
  channel: SuppressionChannelApi;
  idempotencyKey: string;
}): Promise<SuppressionEntryApi> => {
  const body: CreateSuppressionRequestApi = {
    address: params.address.trim(),
    channel: params.channel,
  };

  const response = await campaignApiPost<SuppressionEnvelopeApi<SuppressionEntryApi>>(
    '/v1/suppressions',
    body,
    { 'Idempotency-Key': params.idempotencyKey }
  );

  return assertSuppressionSuccess(response, 'Failed to create suppression');
};

export const bulkCreateSuppressions = async (params: {
  entries: CreateSuppressionRequestApi[];
  idempotencyKey: string;
}): Promise<BulkSuppressionDataApi> => {
  const response = await campaignApiPost<SuppressionEnvelopeApi<BulkSuppressionDataApi>>(
    '/v1/suppressions/bulk',
    { entries: params.entries },
    { 'Idempotency-Key': params.idempotencyKey }
  );

  return assertSuppressionSuccess(response, 'Failed to bulk create suppressions');
};

export const deleteSuppression = async (params: {
  itemId: string;
  idempotencyKey: string;
}): Promise<void> => {
  const itemId = params.itemId.trim();
  if (!itemId) throw new Error('Suppression item id is required');

  const response = await campaignApiDelete<SuppressionEnvelopeApi<null>>(
    `/v1/suppressions/${encodeURIComponent(itemId)}`,
    undefined,
    { 'Idempotency-Key': params.idempotencyKey }
  );

  if (!response || typeof response !== 'object') {
    throw new Error('Failed to delete suppression');
  }

  if (response.success === false) {
    throw new Error(formatSuppressionErrorMessage(response, 'Failed to delete suppression'));
  }
};
